import './App.css';
import { useEffect, useRef  } from 'react';

/**
 * LongevitySchedule component displays a table of longevity milestones for salary increases.
 * It calculates and displays the dates for each longevity step based on the appointed date and current longevity.
 *
 * @param {Object} props - The component props.
 * @param {string} props.appointedDate - The date when the employee was appointed (in a format parseable by Date constructor).
 * @param {number} props.longevity - The current longevity of the employee in years.
 * @returns {JSX.Element} A React component that renders a table of longevity milestones and their corresponding dates.
 */
function LongevitySchedule(props) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    let currentDate = new Date();
    let appointedDate = new Date(props.appointedDate);
    let longevity = Math.floor(props.longevity);
    let longevityCodes = [
        "L5<br/>5 year longevity",
        "L10<br/>10 year longevity",
        "L13<br/>13 year longevity",
        "L15<br/>15 year longevity",
        "L18<br/>18 year longevity",
        "L20<br/>20 year longevity",
        "L22<br/>22 year longevity"
    ]
    if(props.jobTitle == "paraprofessionals"){
        longevityCodes = [
            "L5<br/>5 year longevity",
            "L15<br/>15 year longevity"
        ]
    }
    if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
       longevityCodes = [
            "5 but less than 7",
            "7 but less than 10",
            "10 but less than 15",
            "15 but less than 20",
            "20 but less than 22",
            "22 years or more"
        ]
    }
    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
       longevityCodes = [
            "A/B (first year)",
            "A/B",
            "A/B + 5 but less than 7",
            "A/B + 7 but less than 10",
            "A/B + 10 but less than 15",
            "A/B + 15 but less than 20",
            "A/B + 20 but less than 22",
            "A/B + 22 years or more"
        ]
    }
    let futureDates = 0;
    let targetIndex = 0;
    const currentStepRef = useRef(null);
    let error = false;

    // Calculate targetIndex outside of the map function
    useEffect(() => {
        for(let i = 0; i < currentStepRef.current.querySelectorAll("tr").length; i++){
            if(i == targetIndex){
                currentStepRef.current.querySelectorAll("tr")[i].classList.add('current');
            }
            if(i < targetIndex){
                currentStepRef.current.querySelectorAll("tr")[i].classList.add('completed');
            }
        }

    }, [targetIndex])

    // Map through step status object and set corresponding step status
    if(props.currentStep == 4){
        let stepStatus = Object.entries(props.stepStatus).map(([key, value]) => {
            if(key == 'step-3-status'){
                if(value == 'fail'){
                    error = true;
                }
            }
        })
    }
    
  return (
    <>
    <div className={(props.jobTitle == "paraprofessionals" && props.currentStep == 4) && 'content-box salary-schedule'}>
        {props.title && <h3 className='title'>{props.title}</h3>}
        {error == true && <div className="alert-wrapper"><div className='alert warning'><p className='content'>Possible Error</p></div></div>}
        <div className="salary-schedule-container">
        <table className='salary-schedule longevity' ref={currentStepRef}>
            {longevityCodes.map((step, index) => {
                let compleatedDate = '';
                let nextLongevityDate;
                if(props.jobTitle === "paraprofessionals"){
                    if(index == 1){
                        if(longevity >= 15){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 15);
                    }
                    if(index == 0){
                        if(longevity >= 5){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 5);
                    }
                }
                else if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
                    if(index == 5){
                        if(longevity >= 21){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 22);
                    }
                    if(index == 4){
                        if(longevity >= 20){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 20);
                    }
                    if(index == 3){
                        if(longevity >= 15){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 15);
                    }
                    if(index == 2){
                        if(longevity >= 10){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 10);
                    }
                    if(index == 1){
                        if(longevity >= 7){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 7);
                    }
                    if(index == 0){
                        if(longevity >= 5){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 5);
                    }
                }
                else if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                    if(index == 7){
                        if(longevity >= 21){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 22);
                    }
                    if(index == 6){
                        if(longevity >= 20){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 20);
                    }
                    if(index == 5){
                        if(longevity >= 15){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 15);
                    }
                    if(index == 4){
                        if(longevity >= 10){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 10);
                    }
                    if(index == 3){
                        if(longevity >= 7){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 7);
                    }
                    if(index == 2){
                        if(longevity >= 5){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 5);
                    }
                    if(index == 1){
                        const oneYearAfterAppointment = new Date(appointedDate);
                        oneYearAfterAppointment.setFullYear(appointedDate.getFullYear() + 1);

                        const anniversaryYear = oneYearAfterAppointment.getFullYear();
                        let nextJan1st = new Date(anniversaryYear, 0, 1);
                        if (oneYearAfterAppointment >= nextJan1st) {
                            nextJan1st.setFullYear(anniversaryYear + 1);
                        }
                        let nextJuly1st = new Date(anniversaryYear, 6, 1);
                        if (oneYearAfterAppointment >= nextJuly1st) {
                            nextJuly1st.setFullYear(anniversaryYear + 1);
                        }
                        const firstLongevityDate = nextJan1st < nextJuly1st ? nextJan1st : nextJuly1st;
                        const secondLongevityDate = new Date(firstLongevityDate);
                        secondLongevityDate.setFullYear(secondLongevityDate.getFullYear() + 1);

                        if(currentDate >= secondLongevityDate){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = secondLongevityDate;
                    }
                    if(index == 0){
                        const oneYearAfterAppointment = new Date(appointedDate);
                        oneYearAfterAppointment.setFullYear(appointedDate.getFullYear() + 1);

                        const anniversaryYear = oneYearAfterAppointment.getFullYear();
                        let nextJan1st = new Date(anniversaryYear, 0, 1);
                        if (oneYearAfterAppointment >= nextJan1st) {
                            nextJan1st.setFullYear(anniversaryYear + 1);
                        }
                        let nextJuly1st = new Date(anniversaryYear, 6, 1);
                        if (oneYearAfterAppointment >= nextJuly1st) {
                            nextJuly1st.setFullYear(anniversaryYear + 1);
                        }
                        const firstLongevityDate = nextJan1st < nextJuly1st ? nextJan1st : nextJuly1st;

                        if(currentDate >= firstLongevityDate){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = firstLongevityDate;
                    }
                }
                else{
                    if(index == 6){
                        if(longevity >= 21){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 22);
                    }
                    if(index == 5){
                        if(longevity >= 20){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 20);
                    }
                    if(index == 4){
                        if(longevity >= 18){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 18);
                    }
                    if(index == 3){
                        if(longevity >= 15){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 15);
                    }
                    if(index == 2){
                        if(longevity >= 13){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 13);
                    }
                    if(index == 1){
                        if(longevity >= 10){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 10);
                    }
                    if(index == 0){
                        if(longevity >= 5){
                            compleatedDate = 'completed';
                        }
                        nextLongevityDate = new Date(appointedDate).setFullYear(appointedDate.getFullYear() + 5);
                    }
                }

                if(currentDate < nextLongevityDate){
                    futureDates++;
                }

                if(index === (longevityCodes.length - 1)){
                    targetIndex = longevityCodes.length - futureDates - 1;
                }

                return(
                    <tr key={index} className={compleatedDate} data-index={index + 1}>
                        <td>{new Date(nextLongevityDate).toLocaleDateString("en-US", options)}</td>
                        <td><span className='salary-raise'>$</span></td>
                        <td dangerouslySetInnerHTML={{__html: step}}></td>
                    </tr>
                )   
            })}
        </table>
        </div>
        <p className='content'><strong>$</strong> = Salary Increase</p>
    </div>
    </>
  );
}

export default LongevitySchedule;